import { useState, useEffect, useCallback } from "react";

export interface PromptCategory {
  id: number;
  name: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface Prompt {
  id: number;
  category_id: number;
  title: string;
  description: string;
  template: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface PromptCategoryWithPrompts extends PromptCategory {
  prompts: Prompt[];
}

export function usePrompts() {
  const [categories, setCategories] = useState<PromptCategoryWithPrompts[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    setLoading(true);
    fetch("/api/prompts")
      .then((r) => r.json() as Promise<PromptCategoryWithPrompts[]>)
      .then(setCategories)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  async function createCategory(name: string, sort_order?: number) {
    await fetch("/api/prompt-categories", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, sort_order }),
    });
    reload();
  }

  async function updateCategory(id: number, name: string) {
    await fetch(`/api/prompt-categories/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    });
    reload();
  }

  async function deleteCategory(id: number): Promise<Response> {
    const res = await fetch(`/api/prompt-categories/${id}`, {
      method: "DELETE",
    });
    if (res.ok) reload();
    return res;
  }

  async function createPrompt(data: {
    category_id: number;
    title: string;
    description: string;
    template: string;
    sort_order?: number;
  }) {
    await fetch("/api/prompts", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    reload();
  }

  async function updatePrompt(
    id: number,
    data: Partial<{
      category_id: number;
      title: string;
      description: string;
      template: string;
      sort_order: number;
    }>,
  ) {
    await fetch(`/api/prompts/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    reload();
  }

  async function deletePrompt(id: number) {
    await fetch(`/api/prompts/${id}`, { method: "DELETE" });
    reload();
  }

  return {
    categories,
    loading,
    reload,
    createCategory,
    updateCategory,
    deleteCategory,
    createPrompt,
    updatePrompt,
    deletePrompt,
  };
}
